import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@shared/components/ui/dialog';
import { Card, CardContent } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Checkbox } from '@shared/components/ui/checkbox';
import { 
  Users, Edit, Search, 
  Mail, Shield, CheckCircle2, XCircle, Clock,
  RefreshCw, Loader2, UserPlus, GraduationCap, History, KeyRound, MapPin,
  Eye, EyeOff
} from 'lucide-react';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';
import {
  getAllUsers,
  inviteUser,
  resendInvite,
  updateUserProfile,
  deactivateUser,
  reactivateUser,
  resetPasswordForUser,
} from '@shared/services/authService';
import LoginHistoryModal from '@/components/LoginHistoryModal';
import { debug } from '@shared/utils/debug';
import { 
  isAdmin, 
  canInviteUsers, 
  canEditUserRoles, 
  canDeactivateUsers,
  canResendPassword 
} from '@shared/utils/permissions';
import { useFeatureAccess, FEATURES } from '@shared/hooks/useFeatureAccess';
import { COMPETENCY_LEVELS, getCompetencyLabel } from '@shared/utils/signOffPolicy';
import { getRoleLabel } from '@shared/constants/roleLabels';
import { 
  getAllSites,
  getUserSiteAssignments, 
  updateUserSiteAssignments,
} from '@shared/services/siteAccessService';

const log = debug.withPrefix('UserManagement');

// ============================================================
// Status Badge Components
// ============================================================

const UserStatusBadge = ({ status }) => {
  const config = {
    active: { className: 'bg-green-500 text-white', icon: CheckCircle2, label: 'Active' },
    pending: { className: 'bg-amber-500 text-white', icon: Clock, label: 'Pending Invite' },
    inactive: { className: 'bg-gray-500 text-white', icon: XCircle, label: 'Inactive' },
  };
  
  const { className, icon: Icon, label } = config[status] || config.inactive;
  
  return (
    <Badge className={`${className} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

const RoleBadge = ({ role, roleLabels = {}, customRole = null }) => {
  const config = {
    'system-admin': { className: 'bg-purple-600 text-white' },
    'owner':        { className: 'bg-purple-600 text-white' },
    'admin':        { className: 'bg-amber-600 text-white' },
    'employee':     { className: 'bg-slate-500 text-white' },
  };

  const { className } = config[role] || config.employee || { className: 'bg-slate-500 text-white' };
  const label = getRoleLabel(role, roleLabels);

  return (
    <Badge variant="outline" className={className}>
      {['admin', 'owner', 'system-admin'].includes(role) ? <Shield className="w-3 h-3 mr-1" /> : null}
      {label}{customRole ? ` (${customRole})` : ''}
    </Badge>
  );
};

// ============================================================
// Invite User Modal
// ============================================================

const InviteUserModal = ({ isOpen, onClose, currentUser, onSuccess, competencyEnabled = false, sites = [], siteLabel = 'Site', roleLabels = {} }) => {
  const { hasFeature } = useFeatureAccess();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    surname: '',
    role: 'employee',
    company: '',
    competencyLevel: 1,
    tempPassword: '',
  });
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [availableSites, setAvailableSites] = useState([]);

  // Always load sites when modal opens
  useEffect(() => {
    const loadSites = async () => {
      if (!isOpen) return;
      setIsLoadingSites(true);
      try {
        if (sites.length > 0) {
          setAvailableSites(sites);
        } else {
          const allSites = await getAllSites();
          setAvailableSites(allSites);
        }
      } catch (err) {
        console.error('Failed to load sites:', err);
        setAvailableSites([]);
      } finally {
        setIsLoadingSites(false);
      }
    };
    loadSites();
  }, [isOpen, sites]);

  // Only reset the form when the modal transitions from closed → open.
  // Removing `currentUser` from the dep array prevents the form from
  // wiping itself whenever the parent re-renders with a new object reference.
  const currentUserCompanyRef = React.useRef(currentUser?.company);
  useEffect(() => {
    currentUserCompanyRef.current = currentUser?.company;
  }, [currentUser]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        firstName: '',
        surname: '',
        role: 'employee',
        company: currentUserCompanyRef.current || '',
        competencyLevel: 1,
        tempPassword: '',
      });
      setShowTempPassword(false);
      setSelectedSiteIds([]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSiteToggle = (siteId) => {
    setSelectedSiteIds(prev => 
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleSelectAllSites = () => {
    if (selectedSiteIds.length === availableSites.length) {
      setSelectedSiteIds([]); // Clear all (unrestricted access)
    } else {
      setSelectedSiteIds(availableSites.map(s => s.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast({ title: 'Email Required', description: 'Please enter an email address.', variant: 'destructive' });
      return;
    }
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }

    if (!formData.tempPassword || formData.tempPassword.trim().length < 8) {
      toast({ title: 'Password Required', description: 'Temporary password must be at least 8 characters.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await inviteUser(formData, currentUser.dbId || currentUser.id);
      
      // If user was created and we have site restrictions, apply them
      if (result?.id && selectedSiteIds.length > 0) {
        try {
          await updateUserSiteAssignments(
            result.id,
            selectedSiteIds,
            currentUser?.dbId || currentUser?.id
          );
        } catch (siteError) {
          log.error('Failed to assign sites:', siteError);
          // Don't fail the invite, just warn
          toast({
            title: 'Note',
            description: `User invited but ${siteLabel.toLowerCase()} access could not be set. Please edit the user to set access.`,
            variant: 'default'
          });
        }
      }
      
      toast({ 
        title: 'User Created', 
        description: `Account created for ${formData.email}. Share the temporary password with them — they will be asked to change it on first login.`,
        duration: 8000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      log.error('Invite error:', error);
      toast({ 
        title: 'Invitation Failed', 
        description: error.message || 'Failed to send invitation.', 
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'employee': return 'View site activity, check-ins, and who is on site';
      case 'admin': return 'Full system access including user and configuration management';
      default: return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground w-[calc(100%-1rem)] sm:w-full max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Invite New User
          </DialogTitle>
          <DialogDescription className="sr-only">
            Send an invitation email to add a new team member
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Details Section */}
          <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              User Details
            </p>

            {/* Email - Primary Field */}
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-xs text-muted-foreground flex items-center gap-2">
                <Mail className="w-3 h-3" />
                Email Address *
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@company.com"
                className="bg-muted/30"
                required
              />
            </div>

            {/* First Name and Surname */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="invite-firstName" className="text-xs text-muted-foreground">
                  First Name
                </Label>
                <Input
                  id="invite-firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  className="bg-muted/30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-surname" className="text-xs text-muted-foreground">
                  Surname
                </Label>
                <Input
                  id="invite-surname"
                  value={formData.surname}
                  onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                  placeholder="Smith"
                  className="bg-muted/30"
                />
              </div>
            </div>
          </div>

          {/* Role & Access Section */}
          <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Role & Access
            </p>

            <div className="space-y-2">
              <Label htmlFor="invite-role" className="text-xs text-muted-foreground flex items-center gap-2">
                <Shield className="w-3 h-3" />
                User Role *
              </Label>
              <select
                id="invite-role"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 bg-muted/30 border border-border rounded-md text-foreground text-sm"
              >
                <option value="employee">User — view site activity and check-ins</option>
                <option value="admin">Admin — full system access</option>
              </select>
              <p className="text-[10px] text-muted-foreground">{getRoleDescription(formData.role)}</p>
            </div>

            {/* Competency Level - Only show for employees when feature is enabled */}
            {competencyEnabled && formData.role === 'employee' && (
              <div className="space-y-2">
                <Label htmlFor="invite-competency" className="text-xs text-muted-foreground flex items-center gap-2">
                  <GraduationCap className="w-3 h-3" />
                  Competency Level
                </Label>
                <select
                  id="invite-competency"
                  value={formData.competencyLevel}
                  onChange={(e) => setFormData(prev => ({ ...prev, competencyLevel: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-muted/30 border border-border rounded-md text-foreground text-sm"
                >
                  <option value={COMPETENCY_LEVELS.TRAINEE}>{getCompetencyLabel(COMPETENCY_LEVELS.TRAINEE)} - All work requires sign-off</option>
                  <option value={COMPETENCY_LEVELS.COMPETENT}>{getCompetencyLabel(COMPETENCY_LEVELS.COMPETENT)} - Can self-sign routine tests</option>
                  <option value={COMPETENCY_LEVELS.SENIOR}>{getCompetencyLabel(COMPETENCY_LEVELS.SENIOR)} - Can self-sign all tests</option>
                  <option value={COMPETENCY_LEVELS.SUPERVISOR}>{getCompetencyLabel(COMPETENCY_LEVELS.SUPERVISOR)} - Can sign off others' work</option>
                </select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="invite-company" className="text-xs text-muted-foreground">
                Company / Organisation
              </Label>
              <Input
                id="invite-company"
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name"
                className="bg-muted/30"
              />
            </div>

            {/* Temporary Password */}
            <div className="space-y-2">
              <Label htmlFor="invite-password" className="text-xs text-muted-foreground flex items-center gap-2">
                <KeyRound className="w-3 h-3" />
                Temporary Password *
              </Label>
              <div className="relative">
                <Input
                  id="invite-password"
                  type={showTempPassword ? 'text' : 'password'}
                  value={formData.tempPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, tempPassword: e.target.value }))}
                  placeholder="Set a temporary password"
                  className="bg-muted/30 pr-10"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowTempPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showTempPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Share this with the user. They will be asked to change it on first login.
              </p>
            </div>
          </div>

          {/* Site Access Section */}
          {(isLoadingSites || availableSites.length > 0) && (
            <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {siteLabel} Access
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAllSites}
                  className="text-xs h-6 px-2"
                >
                  {selectedSiteIds.length === 0 ? 'Restrict Access' : 
                   selectedSiteIds.length === availableSites.length ? 'Clear All' : 'Select All'}
                </Button>
              </div>
              
              <p className="text-[10px] text-muted-foreground">
                {selectedSiteIds.length === 0 
                  ? `✓ User will have access to ALL ${siteLabel.toLowerCase()}s (no restrictions)`
                  : `User will only access ${selectedSiteIds.length} of ${availableSites.length} ${siteLabel.toLowerCase()}(s)`}
              </p>
              
              <div className="max-h-40 overflow-y-auto space-y-2 border border-border/40 rounded-md p-2 bg-muted/20">
                {availableSites.map(site => (
                  <label
                    key={site.id}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/40 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedSiteIds.includes(site.id)}
                      onCheckedChange={() => handleSiteToggle(site.id)}
                    />
                    <span className="text-sm">{site.name}</span>
                    {site.code && (
                      <span className="text-xs text-muted-foreground">
                        ({site.code})
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating User...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Edit User Modal
// ============================================================

const EditUserModal = ({ isOpen, onClose, user, currentUser, onSuccess, competencyEnabled, sites = [], siteLabel = 'Site', roleLabels = {} }) => {
  const { hasFeature } = useFeatureAccess();
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    role: 'employee',
    company: '',
    competencyLevel: 1,
  });
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [availableDepots, setAvailableDepots] = useState([]);
  const [isLoadingDepots, setIsLoadingDepots] = useState(true);

  // Always load sites when modal opens
  useEffect(() => {
    const loadSites = async () => {
      if (!isOpen) return;
      setIsLoadingSites(true);
      try {
        if (sites.length > 0) {
          setAvailableSites(sites);
        } else {
          const allSites = await getAllSites();
          setAvailableSites(allSites);
        }
      } catch (err) {
        console.error('Failed to load sites:', err);
        setAvailableSites([]);
      } finally {
        setIsLoadingSites(false);
      }
    };
    loadSites();
  }, [isOpen, sites]);

  // Load user's current site assignments when modal opens
  useEffect(() => {
    const loadUserSites = async () => {
      if (isOpen && user) {
        setIsLoadingSites(true);
        const { siteIds } = await getUserSiteAssignments(user.id);
        setSelectedSiteIds(siteIds);
        setIsLoadingSites(false);
      }
    };
    loadUserSites();
  }, [isOpen, user]);

  // Load available depots from depots table
  useEffect(() => {
    const loadDepots = async () => {
      if (!isOpen) return;
      setIsLoadingDepots(true);
      try {
        const { data, error } = await supabase
          .from('depots')
          .select('id, name')
          .order('name', { ascending: true });
        if (!error) setAvailableDepots(data || []);
      } catch (err) {
        console.error('Failed to load depots:', err);
      } finally {
        setIsLoadingDepots(false);
      }
    };
    loadDepots();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        firstName: user.firstName || '',
        surname: user.surname || '',
        role: user.role || 'employee',
        company: user.company || '',
        competencyLevel: user.competencyLevel || 1,
        depotId: user.depot_id || '',
      });
    }
  }, [isOpen, user]);

  const handleSiteToggle = (siteId) => {
    setSelectedSiteIds(prev => 
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleSelectAllSites = () => {
    if (selectedSiteIds.length === availableSites.length) {
      setSelectedSiteIds([]); // Clear all (unrestricted access)
    } else {
      setSelectedSiteIds(availableSites.map(s => s.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      // Update user profile
      await updateUserProfile(user.id, formData);
      
      // Update site assignments
      await updateUserSiteAssignments(
        user.id, 
        selectedSiteIds,
        currentUser?.dbId || currentUser?.id
      );
      
      toast({ title: 'User Updated', description: `User profile and ${siteLabel.toLowerCase()} access have been updated.` });
      onSuccess();
      onClose();
    } catch (error) {
      log.error('Update error:', error);
      toast({ title: 'Update Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'employee': return 'View site activity, check-ins, and who is on site';
      case 'admin': return 'Full system access including user and configuration management';
      default: return '';
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground w-[calc(100%-1rem)] sm:w-full max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Edit User
          </DialogTitle>
          <DialogDescription className="sr-only">
            Update user details for {user.email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Info Header */}
          <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30 border border-border/60">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-semibold text-primary">
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium">{user.email}</p>
              <p className="text-xs text-muted-foreground">
                {user.status === 'pending' ? 'Invitation Pending' : user.status === 'active' ? 'Active User' : 'Inactive'}
              </p>
            </div>
          </div>

          {/* User Details Section */}
          <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              User Details
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName" className="text-xs text-muted-foreground">
                  First Name
                </Label>
                <Input
                  id="edit-firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  className="bg-muted/30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-surname" className="text-xs text-muted-foreground">
                  Surname
                </Label>
                <Input
                  id="edit-surname"
                  value={formData.surname}
                  onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                  placeholder="Smith"
                  className="bg-muted/30"
                />
              </div>
            </div>
          </div>

          {/* Role & Access Section */}
          <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Role & Access
            </p>

            <div className="space-y-2">
              <Label htmlFor="edit-role" className="text-xs text-muted-foreground flex items-center gap-2">
                <Shield className="w-3 h-3" />
                User Role
              </Label>
              <select
                id="edit-role"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 bg-muted/30 border border-border rounded-md text-foreground text-sm"
                disabled={user.id === (currentUser?.dbId || currentUser?.id)}
              >
                <option value="employee">User — view site activity and check-ins</option>
                <option value="admin">Admin — full system access</option>
              </select>
              <p className="text-[10px] text-muted-foreground">{getRoleDescription(formData.role)}</p>
              {user.id === (currentUser?.dbId || currentUser?.id) && (
                <p className="text-[10px] text-amber-600">You cannot change your own role</p>
              )}
            </div>

            {/* Competency Level - Only show for employees when feature is enabled */}
            {competencyEnabled && formData.role === 'employee' && (
              <div className="space-y-2">
                <Label htmlFor="edit-competency" className="text-xs text-muted-foreground flex items-center gap-2">
                  <GraduationCap className="w-3 h-3" />
                  Competency Level
                </Label>
                <select
                  id="edit-competency"
                  value={formData.competencyLevel}
                  onChange={(e) => setFormData(prev => ({ ...prev, competencyLevel: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-muted/30 border border-border rounded-md text-foreground text-sm"
                >
                  <option value={COMPETENCY_LEVELS.TRAINEE}>{getCompetencyLabel(COMPETENCY_LEVELS.TRAINEE)} - All work requires sign-off</option>
                  <option value={COMPETENCY_LEVELS.COMPETENT}>{getCompetencyLabel(COMPETENCY_LEVELS.COMPETENT)} - Can self-sign routine tests</option>
                  <option value={COMPETENCY_LEVELS.SENIOR}>{getCompetencyLabel(COMPETENCY_LEVELS.SENIOR)} - Can self-sign all tests</option>
                  <option value={COMPETENCY_LEVELS.SUPERVISOR}>{getCompetencyLabel(COMPETENCY_LEVELS.SUPERVISOR)} - Can sign off others' work</option>
                </select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-company" className="text-xs text-muted-foreground">
                Company / Organisation
              </Label>
              <Input
                id="edit-company"
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                className="bg-muted/30"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-depot" className="text-xs text-muted-foreground flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                Primary Depot
              </Label>
              {isLoadingDepots ? (
                <div className="text-xs text-muted-foreground py-2">Loading depots...</div>
              ) : (
                <select
                  id="edit-depot"
                  value={formData.depotId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, depotId: e.target.value || null }))}
                  className="w-full px-3 py-2 bg-muted/30 border border-border rounded-md text-foreground text-sm"
                >
                  <option value="">— No depot assigned —</option>
                  {availableDepots.map(depot => (
                    <option key={depot.id} value={depot.id}>{depot.name}</option>
                  ))}
                </select>
              )}
              <p className="text-[10px] text-muted-foreground">The depot this user will see on their dashboard</p>
            </div>
          </div>

          {/* Site Access Section */}
          {(isLoadingSites || availableSites.length > 0) && (
            <div className="rounded-md border border-border/60 bg-background/60 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {siteLabel} Access
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAllSites}
                  className="text-xs h-6 px-2"
                >
                  {selectedSiteIds.length === 0 ? 'Restrict Access' : 
                   selectedSiteIds.length === availableSites.length ? 'Clear All' : 'Select All'}
                </Button>
              </div>
              
              {isLoadingSites ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : (
                <>
                  <p className="text-[10px] text-muted-foreground">
                    {selectedSiteIds.length === 0 
                      ? `✓ User can access ALL ${siteLabel.toLowerCase()}s (no restrictions)`
                      : `User can only access ${selectedSiteIds.length} of ${availableSites.length} ${siteLabel.toLowerCase()}(s)`}
                  </p>
                  
                  <div className="max-h-40 overflow-y-auto space-y-2 border border-border/40 rounded-md p-2 bg-muted/20">
                    {availableSites.map(site => (
                      <label
                        key={site.id}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/40 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedSiteIds.includes(site.id)}
                          onCheckedChange={() => handleSiteToggle(site.id)}
                        />
                        <span className="text-sm">{site.name}</span>
                        {site.code && (
                          <span className="text-xs text-muted-foreground">
                            ({site.code})
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Main Component
// ============================================================

const UserManagement = ({ users: usersProp, setUsers: _setUsersProp, currentUser, refreshUsers, roleLabels = {}, sites = [], siteLabel = 'Site', currentSiteId = null }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showAllSites, setShowAllSites] = useState(false); // For owner/system-admin to toggle site filtering
  const [userSiteAssignments, setUserSiteAssignments] = useState({}); // Map of userId -> siteIds[]
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [isLoginHistoryOpen, setIsLoginHistoryOpen] = useState(false);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  
  const { hasFeature } = useFeatureAccess();
  const competencyEnabled = hasFeature(FEATURES.COMPETENCY_SIGNOFF);

  const loadUsers = useCallback(async () => {
    // If Dashboard provides users, use them; otherwise fetch
    if (usersProp) {
      // Filter out platform owners
      let visibleUsers = usersProp.filter(u => !u.is_platform_owner && !u.isPlatformOwner);
      
      // Check if user is owner/system-admin
      const isPrivilegedUser = currentUser?.role === 'owner' || currentUser?.role === 'system-admin';
      
      // Determine if we should filter by site:
      // 1. Must have a specific site selected (currentSiteId not null)
      // 2. Must have sites configured
      // 3. Regular admins: always filter
      // 4. Owner/system-admin: only filter if showAllSites is FALSE
      const shouldFilterBySite = currentSiteId && 
                                  sites.length > 0 && 
                                  (!isPrivilegedUser || !showAllSites);
      
      
      if (shouldFilterBySite) {
        // Get users assigned to current site
        const { data: currentSiteUsers, error: currentError } = await supabase
          .from('user_sites')
          .select('user_id')
          .eq('site_id', currentSiteId);
        
        if (currentError) {
          log.error('Failed to load site users:', currentError);
        } else {
          const currentSiteUserIds = new Set((currentSiteUsers || []).map(us => us.user_id));
          
          // Get ALL user_sites assignments at once for efficiency
          const { data: allUserSites, error: allError } = await supabase
            .from('user_sites')
            .select('user_id');
          
          if (allError) {
            log.error('Failed to load all user sites:', allError);
          } else {
            const usersWithSites = new Set((allUserSites || []).map(us => us.user_id));
            
            // Filter to users who:
            // 1. Are owner/system-admin (can see all users), OR
            // 2. Are assigned to this site, OR
            // 3. Have no site assignments (legacy users, show to all admins)
            visibleUsers = visibleUsers.filter(user => {
              // Owner/system-admin can see all users
              if (user.role === 'owner' || user.role === 'system-admin') {
                return true;
              }
              
              // Check if user is assigned to this site
              if (currentSiteUserIds.has(user.id)) {
                return true;
              }
              
              // If no site assignments (legacy user), show to all admins
              if (!usersWithSites.has(user.id)) {
                return true;
              }
              
              // User is assigned to other sites only - hide from this admin
              return false;
            });
          }
        }
      }
      
      // Load site assignments for all visible users
      if (sites.length > 0) {
        const { data: allAssignments } = await supabase
          .from('user_sites')
          .select('user_id, site_id');
        
        const assignmentsMap = {};
        (allAssignments || []).forEach(a => {
          if (!assignmentsMap[a.user_id]) {
            assignmentsMap[a.user_id] = [];
          }
          assignmentsMap[a.user_id].push(a.site_id);
        });
        setUserSiteAssignments(assignmentsMap);
      }
      
      setUsers(visibleUsers);
      setIsLoading(false);
      return;
    }

    // Fallback: fetch users ourselves
    setIsLoading(true);
    try {
      const data = await getAllUsers();
      // Filter out platform owners - they should be hidden from admin views
      const visibleUsers = data.filter(u => !u.is_platform_owner && !u.isPlatformOwner);
      setUsers(visibleUsers);
    } catch (error) {
      log.error('Failed to load users:', error);
      toast({ title: 'Error', description: 'Failed to load users.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [usersProp, currentSiteId, sites.length, currentUser?.role, showAllSites]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleResendInvite = async (user) => {
    setResendingInvite(user.id);
    try {
      await resendInvite(user.email);
      toast({ title: 'Invite Resent', description: `New invite sent to ${user.email}` });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setResendingInvite(null);
    }
  };

  const handleResetPassword = async (user) => {
    setResettingPassword(user.id);
    try {
      await resetPasswordForUser(user.email);
      toast({ 
        title: 'Password Reset Email Sent', 
        description: `${user.name || user.email} will receive an email to reset their password.` 
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setResettingPassword(null);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive) {
        await deactivateUser(user.id);
        toast({ title: 'User Deactivated', description: `${user.name || user.email} has been deactivated.` });
      } else {
        await reactivateUser(user.id);
        toast({ title: 'User Reactivated', description: `${user.name || user.email} has been reactivated.` });
      }
      // Use Dashboard's refresh if available, otherwise local
      if (refreshUsers) {
        await refreshUsers();
      } else {
        await loadUsers();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleViewLoginHistory = (user) => {
    setLoginHistoryUser(user);
    setIsLoginHistoryOpen(true);
  };

  // Filter users - separate active/pending from deactivated
  const activeUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = !filterRole || user.role === filterRole;
    const isActiveOrPending = user.status === 'active' || user.status === 'pending';
    
    return matchesSearch && matchesRole && isActiveOrPending;
  });

  const deactivatedUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = !filterRole || user.role === filterRole;
    
    return matchesSearch && matchesRole && user.status === 'inactive';
  });

  // Count by status
  const statusCounts = {
    all: users.length,
    active: users.filter(u => u.status === 'active').length,
    pending: users.filter(u => u.status === 'pending').length,
    inactive: users.filter(u => u.status === 'inactive').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage team members and their access</p>
        </div>
        {canInviteUsers(currentUser?.role) && (
          <Button onClick={() => setIsInviteModalOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key: 'all', label: 'Total Users', icon: Users, bgColor: 'bg-blue-500/10', iconColor: 'text-blue-500' },
          { key: 'active', label: 'Active', icon: CheckCircle2, bgColor: 'bg-green-500/10', iconColor: 'text-green-500' },
          { key: 'pending', label: 'Pending', icon: Clock, bgColor: 'bg-amber-500/10', iconColor: 'text-amber-500' },
          { key: 'inactive', label: 'Deactivated', icon: XCircle, bgColor: 'bg-gray-500/10', iconColor: 'text-gray-500' },
        ].map(({ key, label, icon: Icon, bgColor, iconColor }) => (
          <Card 
            key={key}
            className="transition-all hover:shadow-md"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${bgColor} flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusCounts[key]}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select 
          value={filterRole} 
          onChange={(e) => setFilterRole(e.target.value)} 
          className="px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="employee">User</option>
        </select>
        
        {/* Site Filter Toggle for Owner/System-Admin */}
        {(currentUser?.role === 'owner' || currentUser?.role === 'system-admin') && sites.length > 0 && currentSiteId && (
          <Button
            variant={showAllSites ? "default" : "outline"}
            onClick={() => setShowAllSites(!showAllSites)}
            className="whitespace-nowrap"
          >
            <Users className="w-4 h-4 mr-2" />
            {showAllSites ? 'All Users' : `${siteLabel} Only`}
          </Button>
        )}
      </div>

      {/* Active & Pending Users Section */}
      {isLoading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      ) : (
        <>
          {/* Active Users */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold">Active Users</h2>
              <span className="text-sm text-muted-foreground">({activeUsers.length})</span>
            </div>
            
            {activeUsers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Users className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">No active users found</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">User</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Role</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
                        {sites.length > 0 && (
                          <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">{siteLabel}s</th>
                        )}
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {activeUsers.map((user) => {
                        const userSites = userSiteAssignments[user.id] || [];
                        const siteNames = userSites.map(siteId => {
                          const site = sites.find(s => s.id === siteId);
                          return site?.name || 'Unknown';
                        });
                        
                        return (
                          <motion.tr
                            key={user.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            {/* User Info */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm font-semibold text-primary">
                                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm truncate">{user.name || user.email}</div>
                                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            
                            {/* Role */}
                            <td className="py-3 px-4">
                              <RoleBadge 
                                role={user.role} 
                                roleLabels={roleLabels} 
                                customRole={user.custom_role || user.customRole} 
                              />
                            </td>
                            
                            {/* Status */}
                            <td className="py-3 px-4">
                              <UserStatusBadge status={user.status} />
                            </td>
                            
                            {/* Site Assignments */}
                            {sites.length > 0 && (
                              <td className="py-3 px-4">
                                {userSites.length === 0 ? (
                                  <span className="text-xs text-muted-foreground italic">All {siteLabel}s</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {siteNames.map((name, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        <MapPin className="w-3 h-3 mr-1" />
                                        {name}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </td>
                            )}
                            
                            {/* Actions */}
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-1.5">
                                {/* Resend Invite / Reset Password - Always show one slot */}
                                {(user.status === 'pending' || !user.lastLogin) ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResendInvite(user)}
                                    disabled={resendingInvite === user.id || !canResendPassword(currentUser?.role)}
                                    title={canResendPassword(currentUser?.role) ? "Resend Invite" : "No Permission"}
                                    className="h-8 w-8 p-0"
                                  >
                                    {resendingInvite === user.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4" />
                                    )}
                                  </Button>
                                ) : user.status === 'active' && user.authId ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResetPassword(user)}
                                    disabled={resettingPassword === user.id || !canResendPassword(currentUser?.role)}
                                    title={canResendPassword(currentUser?.role) ? "Reset Password" : "No Permission"}
                                    className="h-8 w-8 p-0"
                                  >
                                    {resettingPassword === user.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <KeyRound className="w-4 h-4" />
                                    )}
                                  </Button>
                                ) : (
                                  <div className="h-8 w-8" />
                                )}
                                
                                {/* Edit Button - Always show */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditUser(user)}
                                  disabled={!canEditUserRoles(currentUser?.role)}
                                  title={canEditUserRoles(currentUser?.role) ? "Edit User" : "No Permission"}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                
                                {/* Deactivate Button - Always show slot */}
                                {user.id !== (currentUser?.dbId || currentUser?.id) ? (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleToggleActive(user)}
                                    disabled={!canDeactivateUsers(currentUser?.role)}
                                    title={canDeactivateUsers(currentUser?.role) ? "Deactivate User" : "No Permission"}
                                    className="h-8 w-8 p-0"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <div className="h-8 w-8" />
                                )}
                                
                                {/* Login History - Always show */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleViewLoginHistory(user)}
                                  disabled={!canEditUserRoles(currentUser?.role)}
                                  title={canEditUserRoles(currentUser?.role) ? "View Login History" : "No Permission"}
                                  className="h-8 w-8 p-0"
                                >
                                  <History className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>

          {/* Deactivated Users Section */}
          {deactivatedUsers.length > 0 && (
            <div className="space-y-3 mt-8">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-muted-foreground">Deactivated Users</h2>
                <span className="text-sm text-muted-foreground">({deactivatedUsers.length})</span>
              </div>
              
              <div className="flex gap-3 overflow-x-auto pb-2">
                {deactivatedUsers.map((user) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="opacity-60 border-dashed">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          {/* User Info */}
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gray-500/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg font-semibold text-gray-500">
                                {(user.name || user.email || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold truncate text-muted-foreground">{user.name || user.email}</h3>
                                <RoleBadge role={user.role} roleLabels={roleLabels} />
                              </div>
                              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                            </div>
                          </div>

                          {/* Status & Actions */}
                          <div className="flex items-center gap-1.5 ml-16 sm:ml-0 flex-wrap justify-end sm:justify-start">
                            <UserStatusBadge status={user.status} />
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(user)}
                              disabled={!canEditUserRoles(currentUser?.role)}
                              title={canEditUserRoles(currentUser?.role) ? "Edit User" : "No Permission"}
                              className="w-auto px-2.5"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden lg:inline lg:ml-2">Edit</span>
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleToggleActive(user)}
                              disabled={!canDeactivateUsers(currentUser?.role)}
                              className="bg-green-600 hover:bg-green-700 w-auto px-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={canDeactivateUsers(currentUser?.role) ? "Reactivate User" : "No Permission"}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="hidden lg:inline lg:ml-1">Reactivate</span>
                            </Button>
                          </div>
                        </div>
                        
                        {/* Additional Info */}
                        {user.company && (
                          <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground ml-16">
                            <span>Company: {user.company}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        currentUser={currentUser}
        onSuccess={refreshUsers || loadUsers}
        competencyEnabled={competencyEnabled}
        sites={sites}
        siteLabel={siteLabel}
        roleLabels={roleLabels}
      />

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedUser(null); }}
        user={selectedUser}
        currentUser={currentUser}
        onSuccess={refreshUsers || loadUsers}
        competencyEnabled={competencyEnabled}
        sites={sites}
        siteLabel={siteLabel}
        roleLabels={roleLabels}
      />

      <LoginHistoryModal
        isOpen={isLoginHistoryOpen}
        onClose={() => { setIsLoginHistoryOpen(false); setLoginHistoryUser(null); }}
        user={loginHistoryUser}
      />
    </div>
  );
};

export default UserManagement;


